import { Component, computed, OnInit, signal } from "@angular/core";
import { FormControl, ReactiveFormsModule, UntypedFormControl } from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import { MatDialog } from "@angular/material/dialog";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatIconModule } from "@angular/material/icon";
import { MatInputModule } from "@angular/material/input";
import { PageEvent } from "@angular/material/paginator";
import { ActivatedRoute, Router } from "@angular/router";
import { paginatorConfig } from "@app/app.const";
import { FilterBarComponent } from "@components/filters/filter-bar/filter-bar.component";
import {
  FormAutocompleteComponent,
  FormAutocompleteOption,
} from "@components/filters/form-autocomplete/form-autocomplete.component";
import { PrintLabelsDialogComponent } from "@components/print-labels-dialog/print-labels-dialog.component";
import { RefreshButtonComponent } from "@components/refresh-button/refresh-button.component";
import { DataTableComponent } from "@components/table/data-table/data-table.component";
import {
  TableAction,
  TableColumn,
  TableConfig,
  TableSortEvent,
} from "@components/table/models/table-config.model";
import { AppStateService, RequestService } from "@services/index";
import { debounceTime } from "rxjs/operators";

@Component({
  selector: "fh-order-list",
  templateUrl: "./order-list.component.html",
  styleUrls: ["./order-list.component.css"],
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    FilterBarComponent,
    DataTableComponent,
    FormAutocompleteComponent,
    RefreshButtonComponent,
  ],
})
export class OrderListComponent implements OnInit {
  // State signals
  iso2 = signal<string | null>(null);
  searchTerm = signal<string>("");
  pageIndex = signal<number>(0);
  pageSize = signal<number>(paginatorConfig.initialPageSize);
  sortProp = signal<string>("created_on");
  sortDir = signal<"asc" | "desc">("desc");
  isLoading = signal<boolean>(false);

  // Data signals
  orders = signal<any[]>([]);
  total = signal<number>(0);

  // Form controls
  searchControl = new UntypedFormControl("");
  countryFilterFormControl = new FormControl<string | null>(null);

  // Computed options
  destinationCountries = computed(() => this.appStateService.destinationCountries());

  countryOptions = computed<FormAutocompleteOption<string>[]>(() =>
    this.destinationCountries().map((c) => ({ value: c.iso2, label: c.name }))
  );

  readonly paginatorConfig = paginatorConfig;

  tableConfig = computed<TableConfig<any>>(() => ({
    columns: this.getTableColumns(),
    data: this.orders(),
    total: this.total(),
    loading: this.isLoading(),
    pageSizeOptions: paginatorConfig.pageSizeOptions,
    initialPageSize: paginatorConfig.initialPageSize,
    showFirstLastButtons: true,
    stickyHeader: true,
    highlightRowOnClick: false,
    rowClickable: false,
    detailsRoute: "/order_details/:id",
    getDetailsRouteParams: (row) => ({ id: row.id }),
  }));

  // Shipping labels: orders in any live market.
  tableActions = computed<TableAction<any>[]>(() => [
    {
      icon: "print",
      label: "Print shipping label",
      handler: (row) => this.printLabels([String(row.id)]),
      show: (row) =>
        this.appStateService.liveMarketCodes().includes(String(row.iso2 ?? "").toUpperCase()),
    },
  ]);
  /** The orders on this page in a live market, for "Print labels". */
  pageSgOrderIds = computed(() =>
    this.orders()
      .filter((o: any) =>
        this.appStateService.liveMarketCodes().includes(String(o.iso2 ?? "").toUpperCase())
      )
      .map((o: any) => String(o.id))
  );

  constructor(
    private requestService: RequestService,
    private appStateService: AppStateService,
    private router: Router,
    private route: ActivatedRoute,
    private dialog: MatDialog
  ) {}

  printLabels(orderIds: string[]): void {
    if (!orderIds.length) return;
    this.dialog.open(PrintLabelsDialogComponent, { data: { orderIds }, width: "420px" });
  }

  ngOnInit(): void {
    this.setupSearchControl();
    this.route.queryParams.subscribe((params) => {
      this.pageIndex.set(+(params.pageIndex || 0));
      this.pageSize.set(+(params.pageSize || paginatorConfig.initialPageSize));
      this.iso2.set(params.iso2 || null);
      this.searchTerm.set(params.searchTerm || "");
      this.sortProp.set(params.sortProp || "created_on");
      this.sortDir.set((params.sortDir || "desc") as "asc" | "desc");

      this.countryFilterFormControl.patchValue(this.iso2(), { emitEvent: false });
      this.searchControl.setValue(this.searchTerm(), { emitEvent: false });

      this.loadOrders();
    });
  }

  onCountryChange(iso2: string | null): void {
    this.updateQueryParams({ iso2: iso2 || undefined, pageIndex: 0 });
  }

  onSearch(): void {
    this.updateQueryParams({ searchTerm: this.searchTerm() || undefined, pageIndex: 0 });
  }

  onResetFilters(): void {
    this.searchControl.setValue("");
    this.countryFilterFormControl.setValue(null);
    this.updateQueryParams({
      iso2: undefined,
      searchTerm: undefined,
      pageIndex: 0,
      sortProp: undefined,
      sortDir: undefined,
    });
  }

  onSortChange(event: TableSortEvent): void {
    this.updateQueryParams({
      sortProp: event.prop || undefined,
      sortDir: event.dir || undefined,
      pageIndex: 0,
    });
  }

  onPageChange(event: PageEvent): void {
    this.updateQueryParams({ pageIndex: event.pageIndex, pageSize: event.pageSize });
  }

  getCountryName(iso2: string): string {
    return this.destinationCountries().find((c) => c.iso2 === iso2)?.name || iso2;
  }

  // Bound wrapper so fh-refresh-button gets a stable, correctly-`this`-bound reference.
  refreshOrders = () => this.loadOrders();

  private setupSearchControl(): void {
    this.searchControl.valueChanges.pipe(debounceTime(300)).subscribe((value) => {
      this.searchTerm.set(value || "");
    });
  }

  private loadOrders(): void {
    let apiUrl = `/api/v2/orders?pageIndex=${this.pageIndex()}&pageSize=${this.pageSize()}`;

    if (this.sortProp() && this.sortDir()) {
      apiUrl += `&orderField=${this.sortProp()}&order=${this.sortDir()}`;
    }
    if (this.iso2()) {
      apiUrl += `&iso2=${this.iso2()}`;
    }
    if (this.searchTerm()) {
      apiUrl += `&searchTerm=${encodeURIComponent(this.searchTerm())}`;
    }

    this.isLoading.set(true);
    this.requestService.get(apiUrl).subscribe({
      next: (res: any) => {
        if (res.status === true) {
          this.orders.set(res.data.list || []);
          this.total.set(res.data.totalRecords || 0);
        } else {
          this.orders.set([]);
          this.total.set(0);
        }
        this.isLoading.set(false);
      },
      error: () => {
        this.orders.set([]);
        this.total.set(0);
        this.isLoading.set(false);
      },
    });
  }

  private updateQueryParams(params: any): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: params,
      queryParamsHandling: "merge",
    });
  }

  private getTableColumns(): TableColumn<any>[] {
    return [
      {
        key: "id",
        header: "Order ID",
        sortable: true,
      },
      {
        key: "customer_name",
        header: "Customer",
        sortable: false,
      },
      {
        key: "currency",
        header: "Currency",
        sortable: true,
      },
      {
        key: "iso2",
        header: "Country",
        sortable: true,
        format: (v) => this.getCountryName(v),
      },
      {
        key: "shipping_total",
        header: "Shipping",
        sortable: false,
        type: "number",
        // Only for orders the merchant delivers themselves (Merchant Self
        // Delivery); FishyHub delivers the rest and the fee is not theirs.
        format: (v) => (v == null ? "—" : Number(v).toFixed(2)),
      },
      {
        key: "sub_total",
        header: "Subtotal",
        sortable: false,
        type: "number",
        format: (v) => Number(v || 0).toFixed(2),
      },
      {
        key: "total",
        header: "Total",
        sortable: false,
        type: "number",
        format: (v) => Number(v || 0).toFixed(2),
      },
      {
        key: "created_on",
        header: "Order Date",
        sortable: true,
        type: "date",
        format: (v) => new Date(v).toLocaleString("en-US"),
      },
      {
        key: "remarks",
        header: "Customer Remarks",
        sortable: false,
      },
    ];
  }
}
